import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './dialog';
import { 
  Target, Plus, Trash2, Edit2, CheckCircle2, TrendingUp, 
  Egg, Milk, Droplets, Leaf, DollarSign, Award
} from 'lucide-react';

const GOAL_TYPES = [
  { id: 'eggs_weekly', name: 'Weekly Eggs', unit: 'eggs', icon: Egg, category: 'production' },
  { id: 'eggs_monthly', name: 'Monthly Eggs', unit: 'eggs', icon: Egg, category: 'production' },
  { id: 'eggs_daily', name: 'Daily Eggs (avg)', unit: 'eggs/day', icon: Egg, category: 'production' },
  { id: 'milk_weekly', name: 'Weekly Milk', unit: 'gallons', icon: Milk, category: 'production' },
  { id: 'milk_monthly', name: 'Monthly Milk', unit: 'gallons', icon: Milk, category: 'production' },
  { id: 'milk_daily', name: 'Daily Milk (avg)', unit: 'gal/day', icon: Milk, category: 'production' },
  { id: 'feed_cost_monthly', name: 'Monthly Feed Budget', unit: 'USD', icon: DollarSign, category: 'cost' },
];

const GoalTracker = ({ 
  goals = [], 
  onCreateGoal, 
  onDeleteGoal,
  onEditGoal,
  loading = false 
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [formData, setFormData] = useState({
    goal_type: 'eggs_weekly',
    target_value: '',
    notes: ''
  });

  const getGoalInfo = (typeId) => {
    return GOAL_TYPES.find(t => t.id === typeId) || { name: typeId, unit: '', icon: Target };
  };

  const handleSubmit = () => {
    const goalInfo = getGoalInfo(formData.goal_type);
    const goalData = {
      goal_type: formData.goal_type,
      target_value: parseFloat(formData.target_value),
      unit: goalInfo.unit,
      notes: formData.notes || null
    };
    
    if (editingGoal) {
      onEditGoal?.(editingGoal.id, goalData);
    } else {
      onCreateGoal?.(goalData);
    }
    
    setShowDialog(false);
    setEditingGoal(null);
    setFormData({ goal_type: 'eggs_weekly', target_value: '', notes: '' });
  };

  const openEditDialog = (goal) => {
    setEditingGoal(goal);
    setFormData({
      goal_type: goal.goal_type,
      target_value: goal.target_value.toString(),
      notes: goal.notes || ''
    });
    setShowDialog(true);
  };

  const getProgressColor = (percent, isCostGoal = false) => {
    if (isCostGoal) {
      // For cost goals, being under budget (high percent) is good
      if (percent >= 100) return 'bg-green-500';
      if (percent >= 75) return 'bg-yellow-500';
      return 'bg-red-500';
    }
    // For production goals
    if (percent >= 100) return 'bg-green-500';
    if (percent >= 75) return 'bg-emerald-500';
    if (percent >= 50) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  const isGoalMet = (goal) => {
    if (goal.goal_type === 'feed_cost_monthly') {
      return goal.current_value <= goal.target_value;
    }
    return goal.progress_percent >= 100;
  };

  return (
    <Card className="card-farm" data-testid="goal-tracker">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Production Goals
          </CardTitle>
          <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) {
              setEditingGoal(null);
              setFormData({ goal_type: 'eggs_weekly', target_value: '', notes: '' });
            }
          }}>
            <DialogTrigger asChild>
              <Button size="sm" className="btn-primary" data-testid="add-goal-btn">
                <Plus className="w-4 h-4 mr-1" />
                Add Goal
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingGoal ? 'Edit Goal' : 'Set New Goal'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Goal Type</Label>
                  <Select 
                    value={formData.goal_type} 
                    onValueChange={(v) => setFormData({ ...formData, goal_type: v })}
                  >
                    <SelectTrigger data-testid="goal-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">Production</div>
                      {GOAL_TYPES.filter(t => t.category === 'production').map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name} ({type.unit})
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1 text-xs font-semibold text-muted-foreground mt-2">Budget</div>
                      {GOAL_TYPES.filter(t => t.category === 'cost').map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name} ({type.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Target {getGoalInfo(formData.goal_type).unit}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.target_value}
                    onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
                    placeholder={`Enter target ${getGoalInfo(formData.goal_type).unit}`}
                    className="input-farm"
                    data-testid="goal-target-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="e.g., Spring laying season"
                    className="input-farm"
                  />
                </div>
                
                <Button 
                  onClick={handleSubmit} 
                  className="btn-primary w-full"
                  disabled={!formData.target_value}
                  data-testid="save-goal-btn"
                >
                  {editingGoal ? 'Update Goal' : 'Create Goal'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-24">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : goals.length > 0 ? (
          <div className="space-y-3">
            {goals.map((goal) => {
              const info = getGoalInfo(goal.goal_type);
              const IconComponent = info.icon;
              const isCostGoal = goal.goal_type.includes('cost');
              const met = isGoalMet(goal);
              
              return (
                <div 
                  key={goal.id}
                  className={`p-3 rounded-lg border ${met ? 'border-green-500/50 bg-green-500/5' : 'border-border bg-muted/30'}`}
                  data-testid={`goal-${goal.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${met ? 'bg-green-500/20' : 'bg-primary/20'}`}>
                        {met ? (
                          <Award className="w-4 h-4 text-green-500" />
                        ) : (
                          <IconComponent className="w-4 h-4 text-primary" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm text-foreground">{info.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {goal.current_value?.toLocaleString()} / {goal.target_value?.toLocaleString()} {info.unit}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => openEditDialog(goal)}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => onDeleteGoal?.(goal.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="relative">
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${getProgressColor(goal.progress_percent, isCostGoal)}`}
                        style={{ width: `${Math.min(100, goal.progress_percent || 0)}%` }}
                      />
                    </div>
                    <span className="absolute right-0 -top-5 text-xs font-medium text-foreground">
                      {goal.progress_percent}%
                    </span>
                  </div>
                  
                  {met && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
                      <CheckCircle2 className="w-3 h-3" />
                      Goal achieved!
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6">
            <Target className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground text-sm">No goals set yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Click "Add Goal" to start tracking your farm targets
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GoalTracker;
